import CONFIG from "config/config";

// oppizi-platform fetches the country list via REST at `${API_BASE_URL}/countries`
// (src/Module/Api/Client.js:1192). The campaign-builder uses the same backend,
// so we hit the same endpoint here. No auth header needed — it's a public list.

export type Country = {
  id: number;
  name: string;
  code?: string;
  // backend may return more fields (currency, etc.) — we only need these.
};

/**
 * Allow-list of countries we surface in signup UIs. The backend's full
 * `/countries` list includes seed/test rows (e.g. `Azeroth`) and markets
 * we don't operate in — neither belongs in a customer-facing dropdown.
 *
 * Mirrors oppizi-platform's signup filter exactly (see
 * `Containers/Auth/SignUp/selectors.js:selectCountriesToValues`); keep
 * the two lists in sync when adding/removing markets so a user gets the
 * same options whether they sign up from the platform or the builder.
 * "New Zealand" is intentionally commented out — platform has the same
 * exclusion.
 */
export const SIGNUP_ALLOWED_COUNTRY_NAMES = [
  "United States",
  "Australia",
  "United Kingdom",
  "France",
  "Canada",
  "Belgium",
  // "New Zealand",
  "Netherlands",
  "Germany",
  "Spain",
  "Switzerland",
];

export const getCountries = async (): Promise<Country[]> => {
  const response = await fetch(`${CONFIG.API_BASE_URL}/countries`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Failed to load countries (${response.status})`);
  }
  const data = await response.json();
  // The platform reducer normalizes to `countriesList`; the raw response is an
  // array of country objects. If the backend wraps it (e.g. { data: [...] }),
  // unwrap it here.
  const list: Country[] = Array.isArray(data)
    ? (data as Country[])
    : Array.isArray(data?.data)
    ? (data.data as Country[])
    : [];

  // Apply the same allow-list the platform uses for signup, then sort
  // alphabetically by name. Predictable order, easier for the user to
  // scan, and decouples the UI ordering from however the backend (or
  // the allow-list above) happens to list them.
  const allowed = new Set(SIGNUP_ALLOWED_COUNTRY_NAMES);
  return list.filter((c) => allowed.has(c.name)).sort((a, b) => a.name.localeCompare(b.name));
};
